import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Edit, Trash2, MoreVertical, Image as ImageIcon, Upload, Download, FileDown, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import { usePagination } from '../../hooks/usePagination';
import MediaPicker from '../../components/MediaPicker';
import TablePagination from '../../components/ui/table-pagination';
import { resolveMediaUrl } from '../../utils/mediaUrl';

// Inline helper — small uncontrolled input that adds a custom label.
const CustomLabelInput = ({ onAdd }) => {
  const [text, setText] = React.useState('');
  const [color, setColor] = React.useState('terracotta');
  const submit = () => {
    onAdd(text, color);
    setText('');
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        placeholder="Custom (ej. AGOTADO)"
        className="h-8 w-44 text-xs"
      />
      <select
        value={color}
        onChange={(e) => setColor(e.target.value)}
        className="h-8 text-xs border border-gray-300 rounded px-2"
      >
        <option value="terracotta">Terracota</option>
        <option value="ink">Negro</option>
        <option value="accent">Beige</option>
        <option value="sage">Verde</option>
        <option value="cream">Crema</option>
      </select>
      <button
        type="button"
        onClick={submit}
        className="h-8 px-3 text-xs bg-gray-900 text-white rounded hover:bg-gray-700"
      >
        Agregar
      </button>
    </div>
  );
};

const AdminProductsList = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);
  
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Resolve the display name for a category doc from either the old flat
  // shape ({name: "…"}) or the versioned admin shape
  // ({versions: [{name, language, countryCode}]}). Falls back to the slug
  // so we never render an empty <SelectItem> when neither is populated.
  const getCategoryName = (cat) => {
    if (!cat) return '';
    if (cat.versions?.length) {
      const v = cat.versions.find((x) => x.name) || cat.versions[0];
      if (v?.name) return v.name;
    }
    if (typeof cat.name === 'string' && cat.name) return cat.name;
    if (cat.name?.en) return cat.name.en;
    if (cat.name?.es) return cat.name.es;
    return cat.slug || '';
  };
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  
  // Import/Export states
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    slug: '',
    sku: '',
    categoryIds: [],
    brandId: null,
    images: [],
    isFeatured: false,
    isOnSale: false,
    isReborn: false,
    stockMode: 'unified',
    labels: [],
    status: 'draft',
    versions: []
  });

  // Unified stock: one number for every country, held by the base country's version.
  const baseCountryCode = enabledCountries.find(c => c.isBase)?.code || 'CO';
  // Accordion to open so the admin lands on the one editable stock (the base country's).
  const baseAccordion = (versions) => {
    const i = (versions || []).findIndex(v => v.countryCode === baseCountryCode);
    return `country-${i >= 0 ? i : 0}`;
  };
  const isSharedStock = (versionCountryCode) => formData.stockMode === 'unified' && versionCountryCode !== baseCountryCode;

  // Helper function: Get flag emoji from country code
  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  // Helper function: Generate slug from text
  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const [productsRes, categoriesRes, brandsRes, countriesRes] = await Promise.all([
        fetch(`${API_URL}/api/products/admin/all?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/categories/admin/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/brands?active_only=true`),
        fetch(`${API_URL}/api/localization/countries`)
      ]);

      const productsData = await productsRes.json();
      const categoriesData = await categoriesRes.json();
      const brandsData = await brandsRes.json();
      const countriesData = await countriesRes.json();

      
      setProducts(productsData.products || []);
      setCategories(categoriesData || []);
      setBrands(brandsData || []);
      setEnabledCountries(countriesData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize form for creating new product
  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      currency: country.currency,
      name: '',
      shortDescription: '',
      description: '',
      isAvailable: true,
      sizeVariants: [defaultSizeVariant()],
      shippingInfo: '',
      returnPolicy: ''
    }));

    setFormData({
      slug: '',
      sku: '',
      categoryIds: [],
      brandId: null,
      images: [],
      isFeatured: false,
      isOnSale: false,
      isReborn: false,
      stockMode: 'unified',
      labels: [],
      status: 'draft',
      versions: versions
    });

    setExpandedAccordions([baseAccordion(versions)]);
    setEditingProduct(null);
  };

  // Initialize form for editing existing product
  const initializeFormForEdit = (product) => {
    
    // Merge existing versions with newly enabled countries
    const existingVersions = product.versions || [];
    const mergedVersions = enabledCountries.map(country => {
      // Check if version exists for this country
      const existingVersion = existingVersions.find(v => v.countryCode === country.code);
      
      if (existingVersion) {
        // Return existing version — but backfill sizeVariants for any
        // pre-migration document that somehow still lacks it, so the form
        // never renders with zero size rows.
        return {
          ...existingVersion,
          sizeVariants: existingVersion.sizeVariants?.length
            ? existingVersion.sizeVariants
            : [defaultSizeVariant()]
        };
      } else {
        // Create new empty version for newly enabled country
        return {
          countryCode: country.code,
          language: country.language,
          currency: country.currency,
          name: '',
          shortDescription: '',
          description: '',
          isAvailable: true,
          sizeVariants: [defaultSizeVariant()],
          shippingInfo: '',
          returnPolicy: ''
        };
      }
    });
    
    setFormData({
      slug: product.slug || '',
      sku: product.sku || '',
      categoryIds: product.categoryIds || [],
      brandId: product.brandId || null,
      images: product.images || [],
      isFeatured: product.isFeatured || false,
      isOnSale: product.isOnSale || false,
      isReborn: product.isReborn || false,
      stockMode: product.stockMode || 'per_country',
      labels: product.labels || [],
      status: product.status || 'draft',
      versions: mergedVersions
    });

    setExpandedAccordions([product.stockMode === 'unified' ? baseAccordion(mergedVersions) : 'country-0']);
    setEditingProduct(product);
  };

  // Open dialog (create or edit)
  const handleOpenDialog = (product = null) => {
    if (product) {
      initializeFormForEdit(product);
    } else {
      initializeFormForCreate();
    }
    setIsDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
  };

  // A fresh size variant — price/stock live here now, not on the version.
  // Defaults to "Única" (One Size) so a non-sized product needs zero extra
  // admin work; real sizes just get their `size` renamed/added.
  const defaultSizeVariant = (size = 'Única') => ({
    size,
    sku: '',
    price: 0,
    compareAtPrice: null,
    isAvailable: true,
    inventory: {
      trackInventory: true,
      quantity: 0,
      lowStockThreshold: 5,
      allowBackorder: false
    }
  });

  // Update one field on one size variant of one version. Supports the same
  // "parent.child" dotted-path convention as updateVersionField (used for
  // inventory.quantity etc.)
  const updateSizeVariantField = (versionIndex, variantIndex, field, value) => {
    const newVersions = [...formData.versions];
    const variants = [...(newVersions[versionIndex].sizeVariants || [])];

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      variants[variantIndex] = {
        ...variants[variantIndex],
        [parent]: { ...variants[variantIndex][parent], [child]: value }
      };
    } else {
      variants[variantIndex] = { ...variants[variantIndex], [field]: value };
    }

    newVersions[versionIndex] = { ...newVersions[versionIndex], sizeVariants: variants };

    // Unified stock: what is typed on the base country's size is shown on every country (the server
    // mirrors it on save as well).
    if (field.startsWith('inventory.') && formData.stockMode === 'unified' && newVersions[versionIndex].countryCode === baseCountryCode) {
      const [, child] = field.split('.');
      const size = variants[variantIndex].size;
      newVersions.forEach((v, i) => {
        if (i === versionIndex) return;
        newVersions[i] = {
          ...v,
          sizeVariants: (v.sizeVariants || []).map(sv => sv.size === size ? { ...sv, inventory: { ...sv.inventory, [child]: value } } : sv)
        };
      });
    }
    setFormData({ ...formData, versions: newVersions });
  };

  const addSizeVariant = (versionIndex) => {
    const newVersions = [...formData.versions];
    const variants = [...(newVersions[versionIndex].sizeVariants || [])];
    variants.push(defaultSizeVariant(''));
    newVersions[versionIndex] = { ...newVersions[versionIndex], sizeVariants: variants };
    setFormData({ ...formData, versions: newVersions });
  };

  const removeSizeVariant = (versionIndex, variantIndex) => {
    const newVersions = [...formData.versions];
    const variants = [...(newVersions[versionIndex].sizeVariants || [])];
    if (variants.length <= 1) return; // every version needs at least one size
    variants.splice(variantIndex, 1);
    newVersions[versionIndex] = { ...newVersions[versionIndex], sizeVariants: variants };
    setFormData({ ...formData, versions: newVersions });
  };

  // Update version field
  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newVersions[versionIndex] = {
        ...newVersions[versionIndex],
        [parent]: {
          ...newVersions[versionIndex][parent],
          [child]: value
        }
      };
    } else {
      newVersions[versionIndex] = {
        ...newVersions[versionIndex],
        [field]: value
      };
    }
    
    setFormData({ ...formData, versions: newVersions });
  };

  // Save product (create or update)
  const handleSave = async () => {
    try {
      // Validation
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.name && v.name.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a name is required',
          variant: 'destructive',
        });
        return;
      }

      const versionWithBadSize = formData.versions.find(
        (v) => v.name && v.name.trim() !== '' &&
          (!v.sizeVariants?.length || v.sizeVariants.some((sv) => !sv.size || !sv.size.trim()))
      );
      if (versionWithBadSize) {
        toast({
          title: 'Validation Error',
          description: `Cada talla necesita un nombre (${versionWithBadSize.countryCode})`,
          variant: 'destructive',
        });
        return;
      }

      const token = localStorage.getItem('accessToken');
      let response;

      if (editingProduct) {
        // UPDATE existing product
        const productId = editingProduct.id || editingProduct._id;
        
        if (!productId || productId === 'undefined') {
          throw new Error('Invalid product ID');
        }

        response = await fetch(`${API_URL}/api/products/${productId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        // CREATE new product
        response = await fetch(`${API_URL}/api/products`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save product');
      }

      toast({
        title: 'Success',
        description: editingProduct ? 'Product updated successfully' : 'Product created successfully',
      });

      handleCloseDialog();
      fetchAllData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save product',
        variant: 'destructive',
      });
    }
  };

  // Delete product
  const handleDelete = async (product) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const productId = product.id || product._id;

      const response = await fetch(`${API_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      toast({
        title: 'Success',
        description: 'Product deleted successfully',
      });

      fetchAllData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive',
      });
    }
  };

  // MediaPicker handlers
  const handleOpenMediaPicker = () => {
    setIsDialogOpen(false);
    setIsMediaPickerOpen(true);
  };

  const handleCloseMediaPicker = () => {
    setIsMediaPickerOpen(false);
    setIsDialogOpen(true);
  };

  const handleImageSelect = (imageUrl) => {
    const newImage = {
      original: imageUrl,
      thumbnail: imageUrl,
      medium: imageUrl,
      order: formData.images.length,
      alt: { en: '', es: '' }
    };
    setFormData({ ...formData, images: [...formData.images, newImage] });
    setIsMediaPickerOpen(false);
    setIsDialogOpen(true);
  };

  // Import/Export handlers
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/products/export/csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'Products exported to CSV',
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: 'Error',
        description: 'Failed to export products',
        variant: 'destructive',
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/products/export/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_export.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'Products exported to Excel',
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast({
        title: 'Error',
        description: 'Failed to export products',
        variant: 'destructive',
      });
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportResults(null);

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/products/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import failed');
      }

      const results = await response.json();
      setImportResults(results);

      toast({
        title: 'Import Complete',
        description: `Created: ${results.created}, Updated: ${results.updated}, Failed: ${results.failed}`,
      });

      // Refresh products list
      fetchAllData();
    } catch (error) {
      console.error('Error importing products:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import products',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  // Display helpers
  const getProductDisplayName = (product) => {
    return product.versions?.[0]?.name || 'Unnamed Product';
  };

  const getProductPrices = (product) => {
    if (!product.versions || product.versions.length === 0) return 'No prices';
    return product.versions.map(v => {
      const prices = (v.sizeVariants || []).map(sv => sv.price);
      const hasRange = prices.length > 1 && Math.min(...prices) !== Math.max(...prices);
      return `${hasRange ? 'desde ' : ''}${v.price} ${v.currency}`;
    }).join(' / ');
  };

  const getCountryFlags = (product) => {
    if (!product.versions || product.versions.length === 0) return '—';
    return product.versions.filter(v => v.isAvailable).map(v => getFlagEmoji(v.countryCode)).join(' ');
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      archived: 'bg-red-100 text-red-800',
    };
    return styles[status] || styles.draft;
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const productName = getProductDisplayName(product).toLowerCase();
    const matchesSearch = productName.includes(searchQuery.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.slug?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || product.categoryIds?.includes(categoryFilter);
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Pagination
  const {
    currentPage,
    totalPages,
    currentItems: paginatedProducts,
    goToPage,
    totalItems,
    startIndex,
    endIndex,
  } = usePagination(filteredProducts, 20);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-1">{totalItems} products found</p>
        </div>
        <div className="flex gap-2">
          {/* Import/Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isImporting}>
                {isImporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Import/Export
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleImportClick} className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Import Products
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV} className="cursor-pointer">
                <FileDown className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel} className="cursor-pointer">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          
          <Button onClick={() => handleOpenDialog()} className="bg-[#be1f1a] hover:bg-[#a71b17]">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Import Results Alert */}
      {importResults && (
        <div className={`p-4 rounded-lg ${importResults.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
          <h3 className="font-semibold mb-2">Import Results</h3>
          <div className="grid grid-cols-4 gap-4 text-sm mb-2">
            <div>
              <span className="text-gray-600">Total:</span> <span className="font-semibold">{importResults.total}</span>
            </div>
            <div>
              <span className="text-gray-600">Created:</span> <span className="font-semibold text-green-600">{importResults.created}</span>
            </div>
            <div>
              <span className="text-gray-600">Updated:</span> <span className="font-semibold text-blue-600">{importResults.updated}</span>
            </div>
            <div>
              <span className="text-gray-600">Failed:</span> <span className="font-semibold text-red-600">{importResults.failed}</span>
            </div>
          </div>
          {importResults.errors && importResults.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium">View Errors ({importResults.errors.length})</summary>
              <div className="mt-2 max-h-40 overflow-y-auto">
                {importResults.errors.map((err, idx) => (
                  <div key={idx} className="text-xs text-red-600 py-1">
                    SKU: {err.sku} - {err.error}
                  </div>
                ))}
              </div>
            </details>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setImportResults(null)}
            className="mt-2"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat._id || cat.id} value={cat._id || cat.id}>
                {getCategoryName(cat)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Image</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Prices</TableHead>
              <TableHead>Countries</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                  No products found
                </TableCell>
              </TableRow>
            ) : (
              paginatedProducts.map((product) => (
                <TableRow key={product.id || product._id}>
                  <TableCell>
                    <img
                      src={resolveMediaUrl(product.images?.[0]?.thumbnail || product.images?.[0]?.original) || 'https://placehold.co/60x80'}
                      alt={getProductDisplayName(product)}
                      className="w-12 h-16 object-cover rounded"
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/60x80?text=No+image'; }}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{getProductDisplayName(product)}</p>
                      <p className="text-xs text-gray-500">{product.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{product.sku || '—'}</TableCell>
                  <TableCell className="text-sm">{getProductPrices(product)}</TableCell>
                  <TableCell className="text-lg">{getCountryFlags(product)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(product.status)}`}>
                      {product.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(product)} className="cursor-pointer">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(product)} className="text-red-600 cursor-pointer">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={goToPage}
        />
      </div>

      {/* Add/Edit Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update product details and country versions' : 'Create a new product with country-specific versions'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2 space-y-6 py-4">
            {/* MASTER PRODUCT SECTION */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Master Product Information</h3>
              
              <div className="space-y-4">
                {/* Slug & SKU */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Slug (URL) *</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="leather-jacket"
                    />
                  </div>
                  <div>
                    <Label>SKU</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      placeholder="LJ-001"
                    />
                  </div>
                </div>

                {/* Category & Brand */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={formData.categoryIds[0] || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, categoryIds: value === 'none' ? [] : [value] })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat._id || cat.id} value={cat._id || cat.id}>
                            {getCategoryName(cat)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Brand</Label>
                    <Select
                      value={formData.brandId || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, brandId: value === 'none' ? null : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Brand</SelectItem>
                        {brands.map((brand) => {
                          // Extract name from versioned schema or old schema
                          let displayName = brand.slug;
                          if (brand.versions && brand.versions.length > 0) {
                            const firstVersion = brand.versions.find(v => v.name) || brand.versions[0];
                            displayName = firstVersion?.name || brand.slug;
                          } else if (brand.name) {
                            displayName = brand.name;
                          }
                          
                          return (
                            <SelectItem key={brand._id || brand.id} value={brand._id || brand.id}>
                              {displayName}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Images */}
                <div>
                  <Label>Product Images (Shared)</Label>
                  <div className="mt-2 space-y-2">
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-6 gap-2">
                        {formData.images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={resolveMediaUrl(img.original)}
                              alt={`Product ${index + 1}`}
                              className="w-full h-20 object-cover rounded border"
                              onError={(e) => { e.currentTarget.src = 'https://placehold.co/80x80?text=No+image'; }}
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                              onClick={() => handleRemoveImage(index)}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button type="button" variant="outline" onClick={handleOpenMediaPicker} className="w-full">
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Add Image from Gallery
                    </Button>
                  </div>
                </div>

                {/* Status & Flags */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Status *</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.isFeatured}
                        onCheckedChange={(checked) => setFormData({ ...formData, isFeatured: checked })}
                      />
                      <Label className="font-normal cursor-pointer">⭐ Featured</Label>
                    </div>
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.isOnSale}
                        onCheckedChange={(checked) => setFormData({ ...formData, isOnSale: checked })}
                      />
                      <Label className="font-normal cursor-pointer">🔥 On Sale</Label>
                    </div>
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.isReborn}
                        onCheckedChange={(checked) => setFormData({ ...formData, isReborn: checked })}
                      />
                      <Label className="font-normal cursor-pointer">♻️ Producto ReBorn</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock type: one stock for every country, or each country with its own */}
              <div className="mt-6 border-t pt-6" data-testid="admin-product-stock-mode">
                <Label className="text-base font-semibold mb-2 block">Tipo de stock</Label>
                <Select value={formData.stockMode} onValueChange={(value) => {
                  setFormData({ ...formData, stockMode: value });
                  if (value === 'unified') {
                    const key = baseAccordion(formData.versions);
                    setExpandedAccordions(prev => (prev.includes(key) ? prev : [...prev, key]));
                  }
                }}>
                  <SelectTrigger className="max-w-md" data-testid="stock-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unified">Unificado — un solo stock para todos los países</SelectItem>
                    <SelectItem value="per_country">Por país — cada país tiene su propio stock</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-2">
                  {formData.stockMode === 'unified'
                    ? `El stock se edita en ${baseCountryCode} (país base) y se copia a todos los países. Cuando alguien compra desde cualquier país, baja en todos.`
                    : 'Cada país mantiene su stock; una compra solo descuenta el stock del país del pedido.'}
                </p>
              </div>

              {/* Labels editor — admin-managed badges shown on storefront cards */}
              <div className="mt-6 border-t pt-6" data-testid="admin-product-labels">
                <Label className="text-base font-semibold mb-2 block">Etiquetas / Labels</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Aparecen como sello en la esquina del card. Máx 2-3 por producto.
                </p>

                {/* Existing labels */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.labels || []).map((lbl, i) => (
                    <div
                      key={i}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs uppercase tracking-wider"
                      style={{
                        background:
                          lbl.color === 'ink' ? '#1a1814' :
                          lbl.color === 'accent' ? '#e8d9c5' :
                          lbl.color === 'sage' ? '#7a8b6f' :
                          lbl.color === 'cream' ? '#fefcf7' :
                          '#be1f1a',
                        color: lbl.color === 'accent' || lbl.color === 'cream' ? '#1a1814' : '#fefcf7',
                        border: lbl.color === 'cream' ? '1px solid #d8d2c5' : 'none',
                      }}
                    >
                      <span>{lbl.text}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            labels: formData.labels.filter((_, idx) => idx !== i),
                          })
                        }
                        aria-label={`Remove ${lbl.text}`}
                        className="opacity-70 hover:opacity-100"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {(formData.labels || []).length === 0 && (
                    <span className="text-xs text-gray-400">Sin etiquetas aún</span>
                  )}
                </div>

                {/* Quick add presets + custom */}
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { text: 'NUEVO', color: 'accent' },
                    { text: 'SALE', color: 'terracotta' },
                    { text: 'LIMITADO', color: 'ink' },
                    { text: 'ECO', color: 'sage' },
                    { text: 'PRE-ORDEN', color: 'cream' },
                    { text: 'RESCUED', color: 'accent' },
                  ].map((preset) => (
                    <button
                      key={preset.text}
                      type="button"
                      onClick={() => {
                        if ((formData.labels || []).some((l) => l.text === preset.text)) return;
                        setFormData({
                          ...formData,
                          labels: [...(formData.labels || []), preset],
                        });
                      }}
                      className="px-3 py-1.5 text-xs border border-dashed border-gray-300 hover:border-gray-500 rounded"
                    >
                      + {preset.text}
                    </button>
                  ))}
                  <CustomLabelInput
                    onAdd={(text, color) => {
                      const trimmed = text.trim().toUpperCase();
                      if (!trimmed) return;
                      if ((formData.labels || []).some((l) => l.text === trimmed)) return;
                      setFormData({
                        ...formData,
                        labels: [...(formData.labels || []), { text: trimmed, color }],
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* COUNTRY VERSIONS ACCORDION */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
              <p className="text-sm text-gray-600 mb-4">At least one version is required.</p>

              <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
                {formData.versions.map((version, versionIndex) => {
                  const country = enabledCountries.find(c => c.code === version.countryCode);
                  if (!country) return null;

                  return (
                    <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                          <div className="text-left">
                            <div className="font-semibold">{country.name}</div>
                            <div className="text-sm text-gray-500">
                              {country.languageName} • {country.currency}
                              {version.name && <span className="ml-2 text-blue-600">• {version.name}</span>}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-4">
                          {/* Name */}
                          <div>
                            <Label>Product Name ({country.languageName}) *</Label>
                            <Input
                              value={version.name}
                              onChange={(e) => {
                                updateVersionField(versionIndex, 'name', e.target.value);
                                if (!editingProduct && versionIndex === 0 && !formData.slug) {
                                  setFormData({ ...formData, slug: generateSlug(e.target.value) });
                                }
                              }}
                              placeholder={`Enter name in ${country.languageName}`}
                            />
                          </div>

                          {/* Short Description */}
                          <div>
                            <Label>Short Description</Label>
                            <Textarea
                              value={version.shortDescription}
                              onChange={(e) => updateVersionField(versionIndex, 'shortDescription', e.target.value)}
                              rows={2}
                            />
                          </div>

                          {/* Description */}
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={version.description}
                              onChange={(e) => updateVersionField(versionIndex, 'description', e.target.value)}
                              rows={4}
                            />
                          </div>

                          {/* Sizes — price and stock live on each size variant now,
                              not on the version. A product with no real size
                              variation just keeps the single default "Única" row. */}
                          <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold">Tallas y precios ({country.name})</h4>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => addSizeVariant(versionIndex)}
                              >
                                <Plus className="h-4 w-4 mr-1" /> Agregar talla
                              </Button>
                            </div>

                            {version.priceMode === 'reference' && (
                              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                {t('adminProductsPricing.referenceBanner', {
                                  base: version.priceRef?.base ?? '',
                                  percent: version.priceRef?.percent ?? 0,
                                  rate: version.priceRef?.rate ?? 1,
                                })}
                              </div>
                            )}
                            {version.priceMode === 'base' && (
                              <div className="mb-3 inline-block rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700">
                                {t('adminProductsPricing.baseBadge')}
                              </div>
                            )}

                            <div className="space-y-3">
                              {(version.sizeVariants || []).map((variant, variantIndex) => (
                                <div key={variantIndex} className="border rounded-lg p-3 bg-gray-50">
                                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                                    <div className="md:col-span-1">
                                      <Label>Talla *</Label>
                                      <Input
                                        placeholder="S, M, L, XL..."
                                        value={variant.size}
                                        onChange={(e) => updateSizeVariantField(versionIndex, variantIndex, 'size', e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <Label>Precio ({version.currency || country.currency}) *</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={variant.price}
                                        disabled={version.priceMode === 'reference'}
                                        readOnly={version.priceMode === 'reference'}
                                        className={version.priceMode === 'reference' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
                                        title={version.priceMode === 'reference' ? t('adminProductsPricing.readOnlyHint') : undefined}
                                        onChange={(e) => updateSizeVariantField(versionIndex, variantIndex, 'price', parseFloat(e.target.value) || 0)}
                                      />
                                    </div>
                                    <div>
                                      <Label>Precio anterior</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={variant.compareAtPrice || ''}
                                        disabled={version.priceMode === 'reference'}
                                        readOnly={version.priceMode === 'reference'}
                                        className={version.priceMode === 'reference' ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
                                        title={version.priceMode === 'reference' ? t('adminProductsPricing.readOnlyHint') : undefined}
                                        onChange={(e) => updateSizeVariantField(versionIndex, variantIndex, 'compareAtPrice', e.target.value ? parseFloat(e.target.value) : null)}
                                      />
                                    </div>
                                    <div>
                                      <Label>Stock{isSharedStock(version.countryCode) ? ` (compartido con ${baseCountryCode})` : ''}</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={variant.inventory.quantity}
                                        disabled={isSharedStock(version.countryCode)}
                                        className={isSharedStock(version.countryCode) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
                                        title={isSharedStock(version.countryCode) ? `Stock unificado: se edita en ${baseCountryCode}` : undefined}
                                        onChange={(e) => updateSizeVariantField(versionIndex, variantIndex, 'inventory.quantity', parseInt(e.target.value) || 0)}
                                      />
                                    </div>
                                    <div>
                                      <Label>Alerta stock bajo</Label>
                                      <Input
                                        type="number"
                                        min="0"
                                        value={variant.inventory.lowStockThreshold}
                                        disabled={isSharedStock(version.countryCode)}
                                        className={isSharedStock(version.countryCode) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}
                                        onChange={(e) => updateSizeVariantField(versionIndex, variantIndex, 'inventory.lowStockThreshold', parseInt(e.target.value) || 5)}
                                      />
                                    </div>
                                    <div className="flex items-center justify-end">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        disabled={(version.sizeVariants || []).length <= 1}
                                        onClick={() => removeSizeVariant(versionIndex, variantIndex)}
                                        title={(version.sizeVariants || []).length <= 1 ? 'Debe quedar al menos una talla' : 'Eliminar talla'}
                                      >
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6 mt-3">
                                    <div className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={variant.inventory.allowBackorder}
                                        disabled={isSharedStock(version.countryCode)}
                                        onCheckedChange={(checked) => updateSizeVariantField(versionIndex, variantIndex, 'inventory.allowBackorder', checked)}
                                      />
                                      <Label className="font-normal cursor-pointer">Permitir pedidos sin stock</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Checkbox
                                        checked={variant.isAvailable}
                                        onCheckedChange={(checked) => updateSizeVariantField(versionIndex, variantIndex, 'isAvailable', checked)}
                                      />
                                      <Label className="font-normal cursor-pointer">Talla disponible</Label>
                                    </div>
                                  </div>

                                  {/* Medidas — all optional; admins fill in only what's
                                      relevant to the garment (e.g. a jacket can skip "Cadera"). */}
                                  <div className="mt-3 pt-3 border-t border-dashed">
                                    <Label className="text-xs text-gray-500">Medidas (cm)</Label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
                                      {[
                                        { key: 'bust', label: 'Busto' },
                                        { key: 'waist', label: 'Cintura' },
                                        { key: 'hip', label: 'Cadera' },
                                        { key: 'length', label: 'Altura' },
                                      ].map(({ key, label }) => (
                                        <div key={key}>
                                          <Label className="text-xs font-normal">{label}</Label>
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            placeholder="—"
                                            value={variant.measurements?.[key] ?? ''}
                                            onChange={(e) =>
                                              updateSizeVariantField(
                                                versionIndex,
                                                variantIndex,
                                                `measurements.${key}`,
                                                e.target.value ? parseFloat(e.target.value) : null
                                              )
                                            }
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Shipping & Returns */}
                          <div className="border-t pt-4">
                            <h4 className="font-semibold mb-3">Shipping & Returns ({country.name})</h4>
                            <div className="space-y-4">
                              <div>
                                <Label>Shipping Information</Label>
                                <Textarea
                                  value={version.shippingInfo || ''}
                                  onChange={(e) => updateVersionField(versionIndex, 'shippingInfo', e.target.value)}
                                  placeholder="E.g., Free shipping on orders over $100. Standard delivery 3-5 business days."
                                  rows={3}
                                />
                              </div>
                              <div>
                                <Label>Return Policy</Label>
                                <Textarea
                                  value={version.returnPolicy || ''}
                                  onChange={(e) => updateVersionField(versionIndex, 'returnPolicy', e.target.value)}
                                  placeholder="E.g., 30-day return policy. Items must be unused and in original condition."
                                  rows={3}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Availability */}
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={version.isAvailable}
                              onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                            />
                            <Label className="font-normal cursor-pointer">✓ Available in {country.name}</Label>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>{/* End scrollable content */}

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {editingProduct ? 'Update' : 'Create'} Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MediaPicker */}
      <MediaPicker
        isOpen={isMediaPickerOpen}
        onClose={handleCloseMediaPicker}
        onSelect={handleImageSelect}
        fileType="image"
      />
    </div>
  );
};

export default AdminProductsList;
